import '@mui/material/types/OverridableComponentAugmentation'
import { ApplicationTypes, ProductPresets, configuratorBuilder } from 'metalworks-domain'
import { createI18n } from 'metalworks-i18n'

import { initConsoleLogger } from '@salsita-ai/platform-common/utils'
import { Application } from '@salsita-ai/platform-project-sdk/admin'
import { mountApplication } from '@salsita-ai/platform-project-sdk/application'
import {
  createAutoSelectionTransformer,
  createResetSelectionOnNavigationTransformer,
} from '@salsita-ai/platform-project-sdk/domain'
import { initDatadogLogger } from '@salsita-ai/platform-project-sdk/react'

import { version } from '../package.json'
import { adminComponents } from './admin/admin.js'
import { anchorPointComponents } from './entity-components/anchor-point.js'
import { boltHoleComponents } from './entity-components/bolt-hole.js'
import { camlockHoleComponents } from './entity-components/camlock-hole.js'
import { cornerBoltHoleComponents } from './entity-components/corner-bolt-hole'
import { cornerCountersinkHoleComponents } from './entity-components/corner-countersink-hole'
import { countersinkHoleComponents } from './entity-components/countersink-hole.js'
import { deadboltHoleComponents } from './entity-components/deadbolt-hole'
import { euroCylinderHoleComponents } from './entity-components/euro-cylinder-hole.js'
import { holeComponents } from './entity-components/feature.js'
import { hole54Components } from './entity-components/hole-54.js'
import { kickPlateBoltHoleComponents } from './entity-components/kick-plate-bolt-holes'
import { kickPlateCountersinkHoleComponents } from './entity-components/kick-plate-countersink-holes'
import { ovalCylinderHoleComponents } from './entity-components/oval-cylinder-hole.js'
import { pd7HoleComponents } from './entity-components/pd7-hole.js'
import { plainCylinderHoleComponents } from './entity-components/plain-cylinder-hole.js'
import { plainLocksetHoleComponents } from './entity-components/plain-lockset-hole.js'
import { plateComponents } from './entity-components/plate.js'
import { pn6HoleComponents } from './entity-components/pn6-hole.js'
import { rectangularHoleComponents } from './entity-components/rectangular-hole'
import { rimCylinderHoleComponents } from './entity-components/rim-cylinder-hole.js'
import { squareHoleComponents } from './entity-components/square-hole'
import { MetalworksSessionReadyProvider } from './metalworks-init.js'
import { metalworksTheme } from './metalworksTheme.js'
import { AppEnvironment } from './scene/AppEnvironment'
import { analyticsService } from './systems/analytics'
import {
  fixTStrikeInsufficientSizeTransformer,
  flipAnchorableShapePositionTransformer,
  toggleBendablePlateBendTransformer,
  updateArmLengthsTransformer,
  updateFlatPlateLengthOnBendEndLengthChangeTransformer,
  updateFlatPlateLengthOnBendStartLengthChangeTransformer,
  updatePlateMaterialOnBendChangeTransformer,
} from './systems/transformers/bending-transformer'
import { copyFeatureAttributesOnReplaceTransformer } from './systems/transformers/copy-feature-attributes-on-replace-transformer'
import { fixInvariantsTransformer } from './systems/transformers/fix-invariants-transformer'
import { ignoreKickPlateFeatureDetectionTransformer } from './systems/transformers/ignore-kick-plate-feature-deselection'
import { positionFeatureTransformer } from './systems/transformers/position-feature-transformer.js'
import {
  powderCoatingMaterialTransformer,
  powderCoatingRemoveMountingHoleTransformer,
} from './systems/transformers/powder-coating-transformers'
import { resetToolAndViewOnSummaryAndFinalizationTransformer } from './systems/transformers/reset-tool-and-view-on-summary-and-finalization-transformer'
import {
  tStrikeLengthUpdateTransformer,
  tStrikeTongueLengthUpdateTransformer,
} from './systems/transformers/t-strike-length-transformer'
import { CustomOnLoadProvider } from './ui/CustomOnLoadProvider'
import { Disclaimer } from './ui/Disclaimer'
import { LegalFooter } from './ui/LegalFooter'
import './ui/icons'
import { NotAuthenticatedPage } from './ui/not-authenticated-page.js'

const logger = !phoenix.runtimeConfig.DATADOG_ENABLED
  ? initConsoleLogger()
  : initDatadogLogger({
      site: phoenix.runtimeConfig.DATADOG_SITE,
      clientToken: phoenix.runtimeConfig.DATADOG_CLIENT_TOKEN,
      service: phoenix.runtimeConfig.SERVICE_NAME,
      version,
    })

export const Configurator = (config: any) => () => {
  return (
    <div
      style={{
        width: '100%',
        height: '100%',
      }}
    >
      <Disclaimer />
      <Application config={config} />
    </div>
  )
}

const config = configuratorBuilder
  .withPresets(ProductPresets)
  .withComponentOptics([
    ...plateComponents,
    ...holeComponents,
    ...boltHoleComponents,
    ...cornerBoltHoleComponents,
    ...kickPlateBoltHoleComponents,
    ...kickPlateCountersinkHoleComponents,
    ...plainCylinderHoleComponents,
    ...plainLocksetHoleComponents,
    ...camlockHoleComponents,
    ...countersinkHoleComponents,
    ...cornerCountersinkHoleComponents,
    ...euroCylinderHoleComponents,
    ...ovalCylinderHoleComponents,
    ...pn6HoleComponents,
    ...pd7HoleComponents,
    ...hole54Components,
    ...rimCylinderHoleComponents,
    ...deadboltHoleComponents,
    ...rectangularHoleComponents,
    ...squareHoleComponents,
    ...anchorPointComponents,
  ])
  .withAdminComponentOptics(adminComponents)
  .withWebComponentTag('metalworks-configurator')
  .withWrappedApplication(Configurator)
  .buildConfig({
    isDev: phoenix.runtimeConfig.isDev,
    domain: {},
    deploymentId: phoenix.runtimeConfig.DEPLOYMENT_ID,
    persistence: phoenix.runtimeConfig.USE_LOCAL_STORAGE_CLIENT
      ? { type: 'local-storage' }
      : {
          type: 'server',
          serverUrl: phoenix.runtimeConfig.SERVER_URL,
        },
    canvas: {
      highlightingDisabled: true,
      canvasEnvironment: <AppEnvironment />,
    },
    chat: {
      enabled: false,
    },
    features: {
      share: {
        disabled: true,
      },
    },
    ui: {
      OnSessionReadyProvider: MetalworksSessionReadyProvider,
      OnLoadProvider: CustomOnLoadProvider,
      theme: metalworksTheme,
      footer: <LegalFooter />,
      notAuthenticatedPage: <NotAuthenticatedPage />,
    },
    services: {
      analyticsService,
      logger,
      i18n: createI18n(),
    },
    createTransformers: ctx => [
      powderCoatingRemoveMountingHoleTransformer.register(ctx),
      // Priority is 100+ to ensure that these transformers are executed after internal PHX transformers.
      { priority: 100.0, transformer: copyFeatureAttributesOnReplaceTransformer() },
      { priority: 101.1, transformer: positionFeatureTransformer(ctx) },
      { priority: 102, transformer: fixInvariantsTransformer(ctx) },
      // This transformer runs with prio 100 in the navigation system, but it's still needed here.
      { priority: 106.0, transformer: createAutoSelectionTransformer(ctx.navigationSystem) },
      {
        priority: 107.0,
        transformer: createResetSelectionOnNavigationTransformer(
          [
            ApplicationTypes.NavigationState.Dimensions,
            ApplicationTypes.NavigationState.Features,
            ApplicationTypes.NavigationState.FeatureList,
            ApplicationTypes.NavigationState.Summary,
          ],
          ctx
        ),
      },
      resetToolAndViewOnSummaryAndFinalizationTransformer.register(ctx),
      toggleBendablePlateBendTransformer.register(ctx),
      tStrikeLengthUpdateTransformer.register(ctx),
      tStrikeTongueLengthUpdateTransformer.register(ctx),
      flipAnchorableShapePositionTransformer.register(ctx),
      updateArmLengthsTransformer.register(ctx),
      updateFlatPlateLengthOnBendStartLengthChangeTransformer.register(ctx),
      updateFlatPlateLengthOnBendEndLengthChangeTransformer.register(ctx),
      updatePlateMaterialOnBendChangeTransformer.register(ctx),
      fixTStrikeInsufficientSizeTransformer.register(ctx),
      ignoreKickPlateFeatureDetectionTransformer.register(ctx),
      powderCoatingMaterialTransformer.register(ctx),
    ],
  })

mountApplication(config)
